import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../../contexts/AuthContext";
import { useProfile } from "../../contexts/ProfileContext";
import { Gift, Calendar, X, Coins, Sparkles, Trophy } from "lucide-react";
import { toast } from "sonner";
import { BORDERS_BY_ID } from "../../data/cosmetics";
import { SFX } from "../../utils/audio";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const DailyRewardModal = () => {
  const { user } = useAuth();
  const { } = useProfile(); // for refresh side-effects later
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);
  const [claiming, setClaiming] = useState(false);

  useEffect(() => {
    if (!user) return;
    let mounted = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/rewards/status`, { withCredentials: true });
        if (!mounted) return;
        setStatus(res.data);
        if (res.data.daily_available || res.data.monthly_available) setOpen(true);
      } catch (e) {}
    })();
    return () => { mounted = false; };
  }, [user]);

  if (!user || !status || !open) return null;
  const daily = status.next_daily_preview;
  const monthly = status.monthly_reward;

  const claim = async (kind) => {
    setClaiming(true);
    try {
      const res = await axios.post(`${API}/rewards/claim-${kind}`, {}, { withCredentials: true });
      const r = res.data.reward;
      SFX.coin?.() || SFX.buy?.();
      toast.success(`Récompense ${kind === "daily" ? "quotidienne" : "mensuelle"} reçue !`, {
        description: `+${r.gold} or, +${r.xp} XP${r.border ? `, bordure "${BORDERS_BY_ID[r.border]?.name || r.border}" débloquée` : ""}`,
        duration: 4500,
      });
      // Refresh status
      const fresh = await axios.get(`${API}/rewards/status`, { withCredentials: true });
      setStatus(fresh.data);
      if (!fresh.data.daily_available && !fresh.data.monthly_available) setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setClaiming(false);
    }
  };

  return (
    <div data-testid="daily-reward-modal" className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-slate-950/85 backdrop-blur-sm">
      <div className="bg-slate-800 border-4 border-slate-950 rounded-3xl p-5 cartoon-shadow-lg-static max-w-md w-full relative">
        <button
          data-testid="close-rewards"
          onClick={() => setOpen(false)}
          className="absolute -top-2 -right-2 bg-rose-500 text-white border-4 border-slate-950 rounded-full w-9 h-9 flex items-center justify-center cartoon-shadow"
        >
          <X className="w-4 h-4" />
        </button>
        <Gift className="w-12 h-12 mx-auto text-yellow-400 mb-2" />
        <h2 className="font-display text-2xl font-bold text-center mb-4">Tes récompenses</h2>

        {status.daily_available && (
          <div className="bg-slate-900 border-2 border-slate-950 rounded-2xl p-3 mb-3">
            <div className="flex items-center justify-between mb-2">
              <p className="font-display font-bold text-yellow-400 flex items-center gap-1"><Calendar className="w-4 h-4" /> Quotidienne</p>
              <p className="text-[11px] text-slate-400">Série : {status.daily_streak} jour{status.daily_streak > 1 ? "s" : ""}</p>
            </div>
            <p className="text-sm text-slate-200 mb-2">Jour <span className="text-yellow-400 font-bold">{daily.day}/7</span> de la semaine</p>
            <div className="flex items-center gap-2 text-xs mb-2 flex-wrap">
              <span className="bg-yellow-400 text-slate-950 border-2 border-slate-950 rounded px-2 py-0.5 font-bold flex items-center gap-1"><Coins className="w-3 h-3" /> +{daily.gold} or</span>
              <span className="bg-fuchsia-500 text-white border-2 border-slate-950 rounded px-2 py-0.5 font-bold flex items-center gap-1"><Sparkles className="w-3 h-3" /> +{daily.xp} XP</span>
              {daily.border && <span className="bg-emerald-500 text-white border-2 border-slate-950 rounded px-2 py-0.5 font-bold">🎀 Bordure offerte</span>}
            </div>
            <button
              data-testid="claim-daily"
              onClick={() => claim("daily")}
              disabled={claiming}
              className="w-full bg-yellow-400 text-slate-950 border-2 border-slate-950 rounded-xl py-2 font-display font-bold uppercase text-sm cartoon-shadow"
            >
              {claiming ? "..." : "Récupérer"}
            </button>
          </div>
        )}

        {status.monthly_available && (
          <div className="bg-gradient-to-br from-amber-700 to-orange-700 border-4 border-slate-950 rounded-2xl p-3">
            <p className="font-display font-bold text-white flex items-center gap-1 mb-2"><Trophy className="w-4 h-4" /> Mensuelle (1×/mois)</p>
            <div className="flex items-center gap-2 text-xs mb-2 flex-wrap">
              <span className="bg-yellow-400 text-slate-950 border-2 border-slate-950 rounded px-2 py-0.5 font-bold flex items-center gap-1"><Coins className="w-3 h-3" /> +{monthly.gold} or</span>
              <span className="bg-fuchsia-500 text-white border-2 border-slate-950 rounded px-2 py-0.5 font-bold flex items-center gap-1"><Sparkles className="w-3 h-3" /> +{monthly.xp} XP</span>
              {monthly.border && <span className="bg-emerald-500 text-white border-2 border-slate-950 rounded px-2 py-0.5 font-bold">🎀 Bordure exclusive</span>}
            </div>
            <button
              data-testid="claim-monthly"
              onClick={() => claim("monthly")}
              disabled={claiming}
              className="w-full bg-yellow-400 text-slate-950 border-2 border-slate-950 rounded-xl py-2 font-display font-bold uppercase text-sm cartoon-shadow"
            >
              {claiming ? "..." : "Récupérer"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
